/*
  Customer review videos in the Loved by row.

  The markup ships without a source. main-product.liquid puts the file on a
  data attribute instead, because a <video> carrying autoplay downloads in
  full the moment it is parsed, and autoplay overrides its own
  preload="metadata" while doing it. The row sits below the fold, so on a
  product page that meant four videos fetched before most visitors had
  scrolled far enough to know they existed.

  The poster frame is a plain attribute and renders immediately, so the row
  looks finished on arrival. Each card gets its source attached, and starts
  playing, only once it comes near the viewport.

  The row is also a horizontal scroller, which this gets for free: cards off
  to the right are not intersecting either, so they stay untouched until
  they are dragged in.
*/
(() => {
  const SELECTOR = '[data-lazy-video]';

  // Enough warning to have the first frame ready by the time a card is
  // actually on screen, without pulling the whole row in at once.
  const MARGIN = '200px';

  function start(video) {
    // Guarded rather than relying on unobserve alone, since the no-observer
    // path below can reach the same card.
    if (video.dataset.videoStarted) return;
    video.dataset.videoStarted = 'true';

    const src = video.dataset.videoSrc;
    if (!src) return;

    const source = document.createElement('source');
    source.src = src;
    if (video.dataset.videoType) source.type = video.dataset.videoType;

    video.appendChild(source);

    // Required: a source added after parsing is not picked up until the
    // element is told to look again.
    video.load();

    /*
      muted is already on the element, which is what allows this without a
      gesture. Browsers still decline sometimes, and the promise rejects
      rather than throwing. A poster frame is a perfectly good outcome, so
      the rejection is swallowed rather than reported.
    */
    const playing = video.play();
    if (playing && playing.catch) playing.catch(() => {});
  }

  function init() {
    const videos = Array.from(document.querySelectorAll(SELECTOR));
    if (!videos.length) return;

    // No observer available: start everything, which is the old behaviour.
    // Worse than lazy, better than a row of posters that never move.
    if (!('IntersectionObserver' in window)) {
      videos.forEach(start);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          start(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: MARGIN }
    );

    videos.forEach((video) => observer.observe(video));
  }

  // Deferred, so parsing is normally finished by now. The check is for the
  // case where it is not, rather than a listener that would never fire.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
